// External Libraries
import React from "react";
import * as Icon from "react-feather";

// Services
import CommunicationService from "../../../services/CommunicationService";
import UtilityService from "../../../services/UtilityService";

// Constants
import COMMUNICATIONS from "../../../constants/Communications";

// Components
import CommunicationCard from "./CommunicationCard";
import CommunicationModal from "./CommunicationModal";
import MeetSyncModal from "./MeetSyncModal";
import ScheduledMeetingCard from "./ScheduledMeetingCard";

// Common Components
import WComponent from "../../common/WComponent";
import Loader from "../../common/loader/Loader";
import Button from "../../common/button/Button";

// Styles
import "./communications.css";

const { alert } = UtilityService;

const POLL_INTERVAL_MS = 5000;
const IN_PROGRESS_STATUSES = ["pending", "processing"];

class CommunicationLog extends WComponent {
	constructor(props) {
		super(props);

		this.state = {
			loading: false,
			communications: [],
			count: 0,
			filter: "all",
			showModal: false,
			showSyncModal: false,
			selectedCommunicationId: null,
			googleConnected: false,
			view: "log",
			meetings: [],
			meetingsLoading: false,
		};
	}

	componentDidMount = () => {
		this._isMounted = true;
		this.fetchCommunications();
		this.fetchGoogleStatus();
	};

	componentWillUnmount = () => {
		this._isMounted = false;
		this._clearStatusPoll();
	};

	_clearStatusPoll = () => {
		if (this._pollTimer) {
			clearTimeout(this._pollTimer);
			this._pollTimer = null;
		}
	};

	_scheduleStatusPoll = rows => {
		this._clearStatusPoll();

		const stillWorking = (rows || []).some(c => IN_PROGRESS_STATUSES.includes(c.status));
		if (!stillWorking || !this._isMounted) return;

		this._pollTimer = setTimeout(() => {
			if (this._isMounted && this.state.view === "log") {
				this.fetchCommunications({ silent: true });
			}
		}, POLL_INTERVAL_MS);
	};

	fetchMeetings = async () => {
		let { clientId } = this.props;

		await this.update({ meetingsLoading: true });

		try {
			const result = await CommunicationService.listMeetings({ clientId });
			if (result) await this.update({ meetings: result.rows });
		} catch (error) {
			console.log(error);
		}

		await this.update({ meetingsLoading: false });
	};

	onViewChange = async view => {
		await this.update({ view });
		if (view === "meetings") {
			this._clearStatusPoll();
			this.fetchMeetings();
		} else {
			this.fetchCommunications({ silent: true });
		}
	};

	onShowLogView = () => this.onViewChange("log");

	onShowMeetingsView = () => this.onViewChange("meetings");

	fetchGoogleStatus = async () => {
		const status = await CommunicationService.getGoogleStatus();
		if (status) await this.update({ googleConnected: status.connected });
	};

	fetchCommunications = async ({ silent = false } = {}) => {
		let { clientId } = this.props;
		let { filter } = this.state;

		if (!silent) await this.update({ loading: true });

		try {
			const type = filter !== "all" ? filter : undefined;
			const result = await CommunicationService.listCommunications({ clientId, type });

			if (result && this._isMounted) {
				await this.update({
					communications: result.rows,
					count: result.count,
				});
				this._scheduleStatusPoll(result.rows);
			}
		} catch (error) {
			console.log(error);
		}

		if (!silent) await this.update({ loading: false });
	};

	onFilterChange = async filterId => {
		await this.update({ filter: filterId });
		this.fetchCommunications();
	};

	onImportFromMeet = () => {
		let { googleConnected } = this.state;

		if (!googleConnected) {
			alert({ type: "error", text: "Connect your Google account in Settings → Edit Profile first." });
			return;
		}

		this.update({ showModal: false, showSyncModal: true });
	};

	onSyncModalClose = () => {
		this.update({ showSyncModal: false });
		this.fetchCommunications();
	};

	onNewEntry = () => {
		this.update({
			selectedCommunicationId: null,
			showModal: true,
		});
	};

	onCardClick = communicationId => {
		this.update({
			selectedCommunicationId: communicationId,
			showModal: true,
		});
	};

	onModalClose = async () => {
		await this.update({ showModal: false, selectedCommunicationId: null });
		this.fetchCommunications();
	};

	renderScheduledMeetings() {
		let { meetings, meetingsLoading } = this.state;

		if (meetingsLoading) {
			return (
				<div className="container-loader">
					<Loader />
				</div>
			);
		}

		if (meetings.length === 0) {
			return (
				<div className="container-empty">
					<div>
						<Icon.Calendar size={64} />
					</div>
					<div>No meetings scheduled yet.</div>
					<div className="comm-log__empty-sub">Use the Schedule Meeting button to set one up.</div>
				</div>
			);
		}

		return (
			<div className="scheduled-meeting-list">
				{meetings.map(m => (
					<ScheduledMeetingCard key={m.id} meeting={m} />
				))}
			</div>
		);
	}

	render() {
		let { loading, communications, filter, showModal, showSyncModal, selectedCommunicationId, view } = this.state;
		let { clientId } = this.props;

		return (
			<div className="container">

				<div className="comm-log__views">
					<div
						className={`comm-log__views__tab${view === "log" ? " comm-log__views__tab--active" : ""}`}
						onClick={this.onShowLogView}
					>
						<Icon.MessageSquare size={15} />
						Communications
					</div>
					<div
						className={`comm-log__views__tab${view === "meetings" ? " comm-log__views__tab--active" : ""}`}
						onClick={this.onShowMeetingsView}
					>
						<Icon.Calendar size={15} />
						Scheduled Meetings
					</div>
				</div>

				{view === "meetings" ? this.renderScheduledMeetings() : (
				<>
				<div className="container-header">
					<div className="container-header-options">
						<Button onClick={this.onNewEntry} view="icon" icon={<Icon.Plus size={18} />} />
						</div>
				</div>

				<div className="comm-log__filters">
					{COMMUNICATIONS.filters.map(f => (
						<div
							key={f.id}
							className={`comm-log__filters__item${filter === f.id ? " comm-log__filters__item--active" : ""}`}
							onClick={() => this.onFilterChange(f.id)}
						>
							{f.label}
						</div>
					))}
				</div>

				{loading && (
					<div className="container-loader">
						<Loader />
					</div>
				)}

				{!loading && communications.length === 0 && (
					<div className="container-empty">
						<div>
							<Icon.MessageSquare size={64} />
						</div>
						<div>No communications logged yet.</div>
						<div className="comm-log__empty-sub">Click Sync to import Google Meet notes, or + to log manually.</div>
					</div>
				)}

				{!loading && communications.length > 0 && (
					<div className="comm-log__list">
						{communications.map(comm => (
							<CommunicationCard key={comm.id} communication={comm} onClick={() => this.onCardClick(comm.id)} />
						))}
					</div>
				)}
				</>
				)}

				<CommunicationModal
					show={showModal}
					clientId={clientId}
					communicationId={selectedCommunicationId}
					onClose={this.onModalClose}
				onImportFromMeet={this.onImportFromMeet}
				/>

				<MeetSyncModal
					show={showSyncModal}
					clientId={clientId}
					onClose={this.onSyncModalClose}
				/>
			</div>
		);
	}
}

export default CommunicationLog;
